import { useMemo } from 'react';

import path from 'path';

import { Stack, Typography } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { ParamKeyValuePair } from 'react-router-dom';

import { Card, DataGrid } from '~components';
import useColDefs from '~components/dataGrids/useColDefs';
import { generalInformation, pagePathSegments, queryParameters } from '~constants';
import Key from '~interfaces/key';
import { Dimens } from '~themes';

const persistenceKey = 'keys';

interface KeysDataGridProps {
	overwrittenClientId?: string;
	isLoading: boolean;
	showTitle: boolean;
	includeOrganisation?: boolean;
	keys: Key[];
}

const KeyGroupKeysDataGrid = ({
	overwrittenClientId,
	isLoading = false,
	showTitle = false,
	includeOrganisation = false,
	keys = [],
}: KeysDataGridProps) => {
	const { t } = useTranslation();
	const apiRef = useGridApiRef();

	const colDefs = useColDefs();
	const columns = useMemo((): GridColDef<Key>[] => {
		return [
			colDefs.id(path.join(pagePathSegments.Keys, ':id', pagePathSegments.General), (value) => {
				return overwrittenClientId ?
						[
							[
								queryParameters.OverwrittenClientId,
								overwrittenClientId ??
									(value as Key).organisationId ??
									generalInformation.manufacturingOrganisationId,
							],
						]
					:	([] as ParamKeyValuePair[]);
			}),
			colDefs.name(
				true,
				path.join(pagePathSegments.Keys, ':id', pagePathSegments.General),
				(value) => {
					return overwrittenClientId ?
							[
								[
									queryParameters.OverwrittenClientId,
									overwrittenClientId ??
										(value as Key).organisationId ??
										generalInformation.manufacturingOrganisationId,
								],
							]
						:	([] as ParamKeyValuePair[]);
				}
			),
			// colDefs.keyType(), Hidden for now as the backend doesn't return it yet.
			colDefs.serialNumber(),
			...(includeOrganisation ?
				[colDefs.organisationName(true, (value) => value.organisationId ?? '')]
			:	[]),
			colDefs.resourceGroup(),
		];
	}, [colDefs, includeOrganisation, overwrittenClientId, t]);

	return (
		<Card>
			<Stack spacing={Dimens.spacingXl}>
				{showTitle && (
					<Typography variant='labelLg'>{t('key.labels.plural', { ns: 'entities' })}</Typography>
				)}
				<DataGrid
					apiRef={apiRef}
					getRowId={(row) => row.id}
					persistenceKey={persistenceKey}
					editMode='row'
					autoHeight
					disableRowSelectionOnClick
					loading={isLoading}
					columns={columns}
					rows={keys}
					paginationMode='client'
					disableRowSelectionExcludeModel
					keepNonExistentRowsSelected
				/>
			</Stack>
		</Card>
	);
};
export default KeyGroupKeysDataGrid;
